import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, unifyingMapFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { unifyingFetch } from "../common/hooks/networkHook";
import { useMemo } from "react";
import { ensureArray } from "../common/utils/arrayUtilities";
import { registerEntityBasedFetch } from "../common/hooks/initHook";

export const memberAccredtationSlice = createSlice({
    name: 'memberAccreditations',
    initialState: {
        data: {}
    },
    reducers: {
        addOrUpdate: (state, action) => {
            action.payload.forEach(entry => {
                state.data[entry.id] = entry.accreds;
            });
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(accred => delete state.data[accred.memberid]);
        },
        clear: (state) => {
            state.data = {};
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => memberAccredtationSlice.getInitialState());
    }
});
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'staffAccreditations', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'staffAccreditations');


// #region network operations
const prefetch = network => {
    return network.get('/api/v1/memberAccreditationPreload', memberAccredtationSlice.actions.addOrUpdate, visualizeFailure('get'));
}
const fetchAccreditation = unifyingFetch(
    memberid => network => network.get('/api/v1/memberAccreditation/' + encodeURIComponent(memberid), data => memberAccredtationSlice.actions.addOrUpdate([{ id: memberid, accreds: data }]), visualizeFailure('get')),
    memberids => prefetch
);
// #endregion

// #region selectors
const selectAllAccreditations = state => state.memberAccreditations.data;
const selectMemberAccreditation = memberid => createSelector([selectAllAccreditations], memberAccreditations => memberAccreditations[memberid] ?? []);
const selectMemberAccreditations = inputMemberids => createSelector([selectAllAccreditations], memberAccreditations => {
    const memberids = ensureArray(inputMemberids);
    return memberids?.reduce((obj, memberid) => {
        obj[memberid] = memberAccreditations[memberid] ?? [];
        return obj;
    }, {})
})

const accredForDate = (accreditations, date) => {
    if (accreditations == null || accreditations.length < 1) {
        return null;
    }
    let mdate = new Date(date);
    return accreditations.find(a => a.expiry == null || mdate < (new Date(a.expiry)));
}
// #endregion

const listen = {
    invalidateMAC: (data, { dispatch }) => {
        if (data?.memberid) {
            memberAccreditationInit.reset(data?.memberid);
            memberAccreditations.reset(data?.memberid);
        } else {
            memberAccreditationInit.clear();
            memberAccreditations.clear();
        }
    }
}

// #region utilities
export const memberAccredToString = (accreditation) => {
    let toReturn = '';
    if (accreditation?.accred) {
        toReturn += accreditation.accred.name;
    }
    if (accreditation?.supp?.length > 0) {
        toReturn += ' + ' + accreditation.supp.map(s => s.name).join(', ');
    }
    return toReturn;
}
export const memberAccredToShortString = (accreditation, addZeroWidthSpaces) => {
    let toReturn = '';
    const space = addZeroWidthSpaces ? '\u200B' : '';
    if (accreditation?.accred) {
        toReturn += accreditation.accred.shortname;
    }
    if (accreditation?.supp?.length > 0) {
        toReturn += space + '+' + space + accreditation.supp.map(s => s.shortname).join(',' + space);
    }
    return toReturn;
}
export const primaryShortString = (accreditation) => accreditation?.acced?.shortname ?? '';
export const supplementalShortString = (accreditation, addZeroWidthSpaces) => {
    const space = addZeroWidthSpaces ? '\u200B' : '';
    if (accreditation?.supp?.length > 0) {
        return accreditation.supp.map(s => s.shortname).join(',' + space);
    }
    return '';
}
export const memberAccreditationMatchesGroup = (ma, group) => {
    return ((group.accreditations?.length ?? 0) === 0 || group.accreditations.some(a => a === ma?.accred?.id))
            && ((group.supplementals?.length ?? 0) === 0 || group.supplementals.every(gs => ma?.supp?.some(ms => ms.id === gs) ?? true));
}
// #endregion

const memberAccreditationInit = initMapFactory(fetchAccreditation, listen, { name: 'memberAccreditationByID', immortal: true });
const memberAccreditations = unifyingMapFactory(prefetch, 'id', memberAccreditationInit, listen, { name: 'memberAccreditationsUnity'});
registerEntityBasedFetch('member', memberAccreditationInit);

export const [useMemberAccreditation, realizeMemberAccreditation] = dataHookFactory(selectMemberAccreditation, memberAccreditations);
export const [useMemberAccreditations] = dataHookFactory(selectMemberAccreditations, memberAccreditations);
export const useMemberAccreditationForDate = (memberid, date) => {
    const memberAccreditation = useMemberAccreditation(memberid);
    const accred = useMemo(() => accredForDate(memberAccreditation, date), [memberAccreditation, date]);
    return accred;
}
export const realizeMemberAccreditationForDate = async (realizationContext, memberid, date) => {
    const memberAccreditation = await realizeMemberAccreditation(realizationContext, memberid);
    return accredForDate(memberAccreditation, date);
}
export const useMemberAccreditationsForDate = (memberids, date) => {
    const memberAccreditations = useMemberAccreditations(memberids);
    const accreds = useMemo(() => Object.entries(memberAccreditations).reduce((obj, [memberid, accreds]) => {
        obj[memberid] = accredForDate(accreds, date);
        return obj;
    }, {}), [memberAccreditations, date]);
    return accreds;
}

export const useMemberAccreditationExpiry = (memberid, ids) => {    
    const memberAccreditations = useMemberAccreditation(memberid);
    
    const expiries = useMemo(() => {
        return ids.reduce((obj, id) => {
            return { ...obj, [id]: memberAccreditations.findLast(entry => entry.accred?.id === id || entry.supp?.some(s => s.id === id))?.expiry }
        }, {});
    }, [ids, memberAccreditations]);
    return expiries;
}
const reducer = { name: memberAccredtationSlice.name, reducer: memberAccredtationSlice.reducer };
export default reducer;
